import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useAuthStore } from '@/store/auth';
import {
  createProfile, updateProfile, getProfileEvidence, SCHEME_LABEL,
  type BrowserProfile, type BrowserScheme, type BrowserWorkspace,
} from '@/api/browserProfiles';

interface Props {
  // null = closed; 'new' = adding to `workspaceId`.
  profile: BrowserProfile | 'new' | null;
  workspaceId: number | null;
  workspaces: BrowserWorkspace[];
  onClose: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';

const empty = {
  name: '', workspace_id: '', scheme: 'none' as BrowserScheme, notes: '',
  google_email: '', recovery_email: '', proxy: '', cookies: '', platform: '',
  password: '', totp_seed: '', backup_codes: '', app_password: '', firebase_max_projects: '1',
};

// The runner's last screenshot of this profile, fetched through the worker:
// it shows a signed-in Google account.
export function SessionShot({ profileId }: { profileId: number }) {
  const [src, setSrc] = useState('');
  const query = useQuery({
    queryKey: ['browser-profile-shot', profileId],
    queryFn: () => getProfileEvidence(profileId),
    staleTime: 60 * 1000,
  });
  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);
  if (query.isError) return <p className="text-xs text-red-400">{(query.error as Error).message}</p>;
  if (!src) return <div className="h-24 animate-pulse bg-surface-muted rounded" />;
  return <img src={src} alt="profile session" className="max-h-64 rounded border border-border" />;
}

export function BrowserProfileEditor({ profile, workspaceId, workspaces, onClose }: Props) {
  // The project limit is a business rule set by super admins; the API ignores
  // it from anyone else.
  const isSuper = useAuthStore((s) => s.admin?.role === 'super_admin');
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState<string | null>(null);
  const [showShot, setShowShot] = useState(false);
  const existing = profile && profile !== 'new' ? profile : null;

  useEffect(() => {
    if (!profile) return;
    setError(null);
    setShowShot(false);
    // Secrets are write-only: inputs start empty, what is stored is described next to them.
    setForm(existing ? {
      ...empty,
      name: existing.name,
      workspace_id: String(existing.workspace_id),
      scheme: existing.scheme,
      notes: existing.notes ?? '',
      google_email: existing.google_email ?? '',
      recovery_email: existing.recovery_email ?? '',
      platform: existing.platform ?? '',
      firebase_max_projects: String(existing.firebase_max_projects ?? 1),
    } : { ...empty, workspace_id: workspaceId ? String(workspaceId) : String(workspaces[0]?.id ?? '') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const saveMut = useMutation({
    // Blank secret fields are omitted: the server keeps what it is not sent.
    mutationFn: () => {
      const input = {
        name: form.name.trim(),
        workspace_id: Number(form.workspace_id),
        scheme: form.scheme,
        notes: form.notes,
        google_email: form.google_email.trim(),
        platform: form.platform,
        firebase_max_projects: Number(form.firebase_max_projects) || 0,
        ...(form.recovery_email.trim() ? { recovery_email: form.recovery_email.trim() } : {}),
        ...(form.proxy.trim() ? { proxy: form.proxy.trim() } : {}),
        ...(form.cookies.trim() ? { cookies: form.cookies.trim() } : {}),
        ...(form.password ? { password: form.password } : {}),
        ...(form.app_password.trim() ? { app_password: form.app_password.trim() } : {}),
        ...(form.totp_seed.trim() ? { totp_seed: form.totp_seed.replace(/\s+/g, '') } : {}),
        ...(form.backup_codes.trim()
          ? { backup_codes: form.backup_codes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean) }
          : {}),
      };
      return existing ? updateProfile(existing.id, input) : createProfile(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['browser-profiles'] });
      qc.invalidateQueries({ queryKey: ['browser-workspaces'] });
      onClose();
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to save the profile'),
  });

  const bound = !!existing?.shardx_profile_id;
  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <Modal isOpen={!!profile} onClose={onClose} title={existing ? `Browser profile — ${existing.name}` : 'New browser profile'} size="2xl">
      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">{error}</div>
      )}
      <p className="text-sm text-content-muted mb-4">
        A Google account with its own ShardX profile on the VPS. Once a proxy is saved the runner creates the
        profile on it, signs in with the password and TOTP, and checks the session every few days. Secrets are
        stored encrypted and never shown again; a field left empty keeps what is stored.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">Name</label>
          <input className={inputCls} value={form.name} onChange={set('name')} placeholder="e.g. FB 01" autoFocus={!existing} />
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">Workspace</label>
          <select className={inputCls} value={form.workspace_id} onChange={set('workspace_id')}>
            {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">Scheme</label>
          <select className={inputCls} value={form.scheme} onChange={set('scheme')}>
            {(Object.keys(SCHEME_LABEL) as BrowserScheme[]).map((s) => <option key={s} value={s}>{SCHEME_LABEL[s]}</option>)}
          </select>
          <p className="text-xs text-content-muted mt-1">
            {form.scheme === 'firebase'
              ? 'Offered under “New Firebase project” in Apps and Apps Setup; the runner makes the project in this account.'
              : 'What this account is for.'}
          </p>
          {form.scheme === 'firebase' && (
            <div className="mt-2">
              <label className="block text-sm text-content-muted mb-1">
                Firebase projects allowed {existing && <span>· {existing.firebase_projects} used</span>}
              </label>
              <input className={inputCls} type="number" min={0} value={form.firebase_max_projects} disabled={!isSuper}
                title={isSuper ? undefined : 'Set by a super admin'} onChange={set('firebase_max_projects')} />
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">Google account email</label>
          <input className={inputCls} value={form.google_email} onChange={set('google_email')} placeholder="someone@gmail.com" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">
            Proxy {existing?.proxy_label && <span>· stored {existing.proxy_label}</span>}
          </label>
          <input className={inputCls} type="password" autoComplete="off" value={form.proxy} onChange={set('proxy')} placeholder="socks5://user:pass@host:port" />
          <p className="text-xs text-content-muted mt-1">
            {bound
              ? <>Profile <span className="font-mono">{existing!.shardx_profile_id}</span> is bound to this proxy for life.</>
              : 'Sticky residential, one per account. The runner creates the profile on it within a minute of saving.'}
          </p>
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">Fingerprint OS</label>
          <select className={inputCls} value={form.platform} disabled={bound} onChange={set('platform')}>
            <option value="">Windows (default)</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">Password {existing?.has_password && <span>· stored</span>}</label>
          <input className={inputCls} type="password" autoComplete="new-password" value={form.password} onChange={set('password')} />
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">TOTP seed {existing?.has_totp && <span>· stored</span>}</label>
          <input className={inputCls} type="password" autoComplete="off" value={form.totp_seed} onChange={set('totp_seed')} placeholder="base32 secret" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-sm text-content-muted mb-1">
            Backup codes {!!existing?.backup_codes_left && <span className={existing.backup_codes_left <= 3 ? 'text-amber-400' : ''}>· {existing.backup_codes_left} left</span>}
          </label>
          <textarea className={`${inputCls} h-20`} value={form.backup_codes} onChange={set('backup_codes')} placeholder="One per line" />
        </div>
        <div>
          <label className="block text-sm text-content-muted mb-1">Recovery email</label>
          <input className={`${inputCls} mb-3`} value={form.recovery_email} onChange={set('recovery_email')} />
          <label className="block text-sm text-content-muted mb-1">App password {existing?.has_app_password && <span>· stored</span>}</label>
          <input className={`${inputCls} mb-3`} type="password" autoComplete="off" value={form.app_password} onChange={set('app_password')} placeholder="16 letters, optional" />
          <label className="block text-sm text-content-muted mb-1">Notes</label>
          <input className={inputCls} value={form.notes} onChange={set('notes')} placeholder="Who it is for, where it came from" />
        </div>
      </div>

      <label className="block text-sm text-content-muted mb-1">
        Cookies <span>· optional</span>
        {existing?.cookies_pending && <span className="text-amber-400"> · waiting for the runner to import</span>}
      </label>
      <div className="flex items-start gap-2 mb-1">
        <textarea
          className="flex-1 px-3 py-2 rounded-md bg-background border border-border text-content font-mono text-xs h-20"
          value={form.cookies}
          onChange={set('cookies')}
          placeholder="Paste a cookie export — JSON array (Cookie-Editor, EditThisCookie) or Netscape cookies.txt"
        />
        <label className="shrink-0 text-xs text-sky-400 hover:underline cursor-pointer pt-2">
          from file
          <input
            type="file"
            accept=".json,.txt,application/json,text/plain"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setForm({ ...form, cookies: await f.text() });
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <p className="text-xs text-content-muted mb-4">
        Imported into the profile on the runner’s next visit and wiped here afterwards.
      </p>

      {existing?.has_session_evidence && (
        <div className="mb-4">
          {showShot
            ? <SessionShot profileId={existing.id} />
            : <button type="button" className="text-xs text-sky-400 hover:underline" onClick={() => setShowShot(true)}>Last session screenshot</button>}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={saveMut.isPending} disabled={!form.name.trim() || !form.workspace_id}
          onClick={() => { setError(null); saveMut.mutate(); }}>
          {existing ? 'Save' : 'Add profile'}
        </Button>
      </div>
    </Modal>
  );
}
